<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    persistent
    max-width="960"
    scrollable
  >
    <v-card class="d-flex flex-column" style="height: 85vh; max-height: 780px">
      <!-- Header -->
      <v-card-title class="py-3 px-5 d-flex align-center flex-shrink-0">
        <v-icon class="mr-2">mdi-api</v-icon>
        <span>{{ isEditMode ? 'Edit' : 'New' }} API Data Source</span>
        <v-spacer />
        <v-btn icon small @click="confirmClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Step Indicator -->
      <div class="px-5 py-2 d-flex align-center flex-shrink-0" style="background: var(--v-bg1-base)">
        <div
          v-for="(stepInfo, idx) in STEP_INFO"
          :key="stepInfo.step"
          class="d-flex align-center"
          :class="{ 'ml-2': idx > 0 }"
        >
          <v-avatar
            :size="28"
            :color="getStepColor(stepInfo.step)"
            class="mr-1"
          >
            <v-icon v-if="stepInfo.step < currentStep" x-small color="white">
              mdi-check
            </v-icon>
            <span v-else class="white--text text-caption">{{ stepInfo.step }}</span>
          </v-avatar>
          <span
            class="text-caption"
            :class="stepInfo.step === currentStep ? 'font-weight-medium' : 'text--secondary'"
          >
            {{ stepInfo.title }}
          </span>
          <v-icon v-if="idx < STEP_INFO.length - 1" x-small class="mx-1 text--disabled">
            mdi-chevron-right
          </v-icon>
        </div>
      </div>

      <v-divider />

      <!-- Step Content -->
      <v-card-text class="flex-grow-1 overflow-y-auto pa-5">
        <!-- Step 1: Configure API -->
        <shared-api-config-step
          v-if="currentStep === WIZARD_STEPS.CONFIGURE_API"
          v-model="wizardConfig"
        />

        <!-- Step 2: Test & Preview -->
        <shared-api-test-preview-step
          v-if="currentStep === WIZARD_STEPS.TEST_PREVIEW"
          ref="testPreviewStep"
          :config="wizardConfig"
          :api-service="apiService"
          @test-success="onTestSuccess"
          @preview-loaded="onPreviewLoaded"
          @update:responseDataPath="onResponseDataPathUpdate"
        />

        <!-- Step 3: Map Columns (slot for module-specific mapping) -->
        <template v-if="currentStep === WIZARD_STEPS.MAP_COLUMNS">
          <slot
            v-if="previewData"
            name="step-mapping"
            :preview-headers="previewData.headers"
            :preview-data="previewData.data"
            :mapping="wizardMapping"
            :on-mapping-complete="onMappingComplete"
            :on-mapping-validity="onMappingValidity"
          />
          <div
            v-if="!previewData"
            class="text-center py-8 text--secondary"
          >
            <v-icon size="48" color="grey lighten-1">mdi-table-off</v-icon>
            <div class="mt-2">
              No preview data available.
              <v-btn text small color="primary" @click="prevStep">
                Go back
              </v-btn>
              and click "Fetch Preview" to load sample data for column mapping.
            </div>
          </div>
        </template>

        <!-- Step 4: Schedule -->
        <shared-schedule-config-step
          v-if="currentStep === WIZARD_STEPS.SCHEDULE"
          v-model="wizardSchedule"
        />

        <!-- Step 5: Review -->
        <shared-review-confirm-step
          v-if="currentStep === WIZARD_STEPS.REVIEW"
          :config="wizardConfig"
          :mapping="wizardMapping"
          :schedule="wizardSchedule"
          :target-field-label="targetFieldLabel"
        />
      </v-card-text>

      <v-divider />

      <!-- Footer Actions -->
      <v-card-actions class="px-5 py-3 flex-shrink-0">
        <v-btn text @click="confirmClose">Cancel</v-btn>
        <v-spacer />
        <v-btn
          v-if="currentStep > 1"
          outlined
          @click="prevStep"
        >
          <v-icon left small>mdi-chevron-left</v-icon> Back
        </v-btn>
        <v-btn
          v-if="currentStep < WIZARD_STEPS.REVIEW"
          color="primary"
          :disabled="!canProceed"
          @click="nextStep"
        >
          Next <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-if="currentStep === WIZARD_STEPS.REVIEW"
          color="primary"
          :loading="saving"
          @click="save"
        >
          <v-icon left small>mdi-content-save</v-icon>
          {{ isEditMode ? 'Update' : 'Save & Activate' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { WIZARD_STEPS, STEP_INFO } from '@/features/Share/config/apiDataSourceSteps';
import SharedApiConfigStep from './SharedApiConfigStep.vue';
import SharedApiTestPreviewStep from './SharedApiTestPreviewStep.vue';
import SharedScheduleConfigStep from './SharedScheduleConfigStep.vue';
import SharedReviewConfirmStep from './SharedReviewConfirmStep.vue';

export default {
  name: 'SharedApiDataSourceWizard',
  components: {
    SharedApiConfigStep,
    SharedApiTestPreviewStep,
    SharedScheduleConfigStep,
    SharedReviewConfirmStep,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editDataSource: {
      type: Object,
      default: null,
    },
    /** Service object with testConnection(config) and fetchPreview(config, maxRows, { signal }) */
    apiService: {
      type: Object,
      required: true,
    },
    /** Label for the target field column in the review step (e.g. 'Register Field', 'System Field') */
    targetFieldLabel: {
      type: String,
      default: 'Target Field',
    },
  },
  data() {
    return {
      WIZARD_STEPS,
      STEP_INFO,
      currentStep: 1,
      saving: false,
      previewData: null,
      testResult: null,
      wizardConfig: this.createDefaultConfig(),
      wizardMapping: null,
      wizardMappingValid: true,
      wizardSchedule: this.createDefaultSchedule(),
    };
  },
  computed: {
    isEditMode() {
      return !!this.editDataSource;
    },
    canProceed() {
      switch (this.currentStep) {
        case WIZARD_STEPS.CONFIGURE_API:
          return !!(this.wizardConfig.name && this.wizardConfig.url);
        case WIZARD_STEPS.TEST_PREVIEW:
          return !!(this.testResult && this.testResult.success);
        case WIZARD_STEPS.MAP_COLUMNS:
          return !!this.wizardMapping && this.wizardMappingValid;
        case WIZARD_STEPS.SCHEDULE:
          return true;
        default:
          return true;
      }
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.initWizard();
      }
    },
    editDataSource(ds) {
      if (ds) {
        this.loadDataSource(ds);
      }
    },
  },
  methods: {
    createDefaultConfig() {
      return {
        name: '',
        url: '',
        httpMethod: 'GET',
        requestBody: '',
        headers: [],
        auth: {
          authType: 0,
          apiKeyHeaderName: '',
          apiKeyValue: '',
          bearerToken: '',
          username: '',
          password: '',
          tokenUrl: '',
          clientId: '',
          clientSecret: '',
          scope: '',
        },
        responseDataPath: '',
        pagination: {
          type: 0,
          pageParamName: 'page',
          pageSizeParamName: 'pageSize',
          pageSizeValue: 100,
          offsetParamName: 'offset',
          limitParamName: 'limit',
          limitValue: 100,
          cursorParamName: 'cursor',
          cursorResponsePath: '',
        },
      };
    },

    createDefaultSchedule() {
      return {
        frequency: 0,
        hour: 0,
        minute: 0,
        dayOfWeek: 1,
        cronExpression: '',
        triggerImmediate: false,
      };
    },

    initWizard() {
      this.currentStep = 1;
      this.testResult = null;
      this.previewData = null;
      this.wizardMappingValid = true;

      if (this.editDataSource) {
        this.loadDataSource(this.editDataSource);
      } else {
        this.resetConfig();
      }
    },

    loadDataSource(ds) {
      this.wizardConfig = {
        name: ds.name || '',
        url: ds.url || '',
        httpMethod: ds.httpMethod || 'GET',
        requestBody: ds.requestBody || '',
        headers: ds.headers ? JSON.parse(JSON.stringify(ds.headers)) : [],
        auth: ds.authConfig
          ? JSON.parse(JSON.stringify(ds.authConfig))
          : { authType: 0 },
        responseDataPath: ds.responseDataPath || '',
        pagination: ds.paginationConfig
          ? JSON.parse(JSON.stringify(ds.paginationConfig))
          : { type: 0 },
      };
      this.wizardMapping = ds.fieldMapping
        ? JSON.parse(JSON.stringify(ds.fieldMapping))
        : null;
      this.wizardSchedule = ds.scheduleConfig
        ? JSON.parse(JSON.stringify(ds.scheduleConfig))
        : this.createDefaultSchedule();
    },

    resetConfig() {
      this.wizardConfig = this.createDefaultConfig();
      this.wizardMapping = null;
      this.wizardSchedule = this.createDefaultSchedule();
    },

    getStepColor(step) {
      if (step < this.currentStep) return 'success';
      if (step === this.currentStep) return 'primary';
      return 'grey lighten-1';
    },

    nextStep() {
      if (this.currentStep < WIZARD_STEPS.REVIEW) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    onTestSuccess(result) {
      this.testResult = result;
    },

    onPreviewLoaded(data) {
      this.wizardMappingValid = true;
      this.previewData = data;
    },

    onResponseDataPathUpdate(path) {
      this.wizardConfig.responseDataPath = path;
    },

    onMappingComplete(mapping) {
      this.wizardMapping = mapping;
    },
    onMappingValidity(valid) {
      this.wizardMappingValid = valid;
    },

    async save() {
      this.saving = true;
      try {
        const payload = {
          name: this.wizardConfig.name,
          connectionConfig: {
            url: this.wizardConfig.url,
            httpMethod: this.wizardConfig.httpMethod,
            requestBody: this.wizardConfig.requestBody || null,
            headers: this.wizardConfig.headers.filter(h => h.key),
            auth: this.wizardConfig.auth,
            responseDataPath: this.wizardConfig.responseDataPath || null,
            pagination: this.wizardConfig.pagination,
          },
          mapping: this.wizardMapping,
          schedule: this.wizardSchedule,
          triggerImmediate: this.wizardSchedule.triggerImmediate,
        };

        // Emit save event for parent wrapper to handle with module-specific logic
        this.$emit('save', {
          payload,
          isEditMode: this.isEditMode,
          editDataSourceId: this.editDataSource?.id,
        });
      } catch (err) {
        this.$dialog.notify.error(
          err.response?.data?.message || 'Failed to save data source'
        );
        this.saving = false;
      }
    },

    /** Called by parent wrapper after successful save */
    onSaveComplete() {
      this.saving = false;
      this.close();
    },

    /** Called by parent wrapper if save fails */
    onSaveError(message) {
      this.saving = false;
      this.$dialog.notify.error(message || 'Failed to save data source');
    },

    confirmClose() {
      const hasProgress = this.currentStep > 1 || this.wizardConfig.name || this.wizardConfig.url;
      if (hasProgress) {
        this.$dialog.confirm({
          text: 'Discard your progress and close?',
          title: 'Close Wizard',
          actions: {
            false: 'Keep Editing',
            true: { text: 'Discard', color: 'error' },
          },
        }).then(confirmed => {
          if (confirmed) this.close();
        });
      } else {
        this.close();
      }
    },

    close() {
      this.$emit('input', false);
      this.currentStep = 1;
      this.testResult = null;
      this.previewData = null;
    },
  },
};
</script>
